import { Data2, useDataTypesHooks } from "../../../features/data-types"
import { O } from "../../../features/ui"
import { TableName } from "../../../tables"
import { useOperationBuilders, useOperationHooks, useOperationService } from "../../operations"
import { useStockService } from "../../stock"
import { ItemDeliveryStats, Operation } from "../../operations/state/types"
import { PurchaseOrder } from "../../purchases"
import { HoardOrder, SaleOperation, useHoardHooks, useSaleOperationLoaders } from "../../sales"
import { hoardHelpers } from "../../sales/hooks/hoardHooks"
import { useDeliveryOrderActions } from "../actions/deliveryOrderActions"
import { useDeliveryOrderBuilders } from "../builders/deliveryOrderBuilders"
import { deliveryOrderHelpers } from "../hooks/deliveryOrderHooks"
import { useDeliveryOrderLoaders } from "../loaders/deliveryOrderLoaders"
import { DeliveryOrder, DeliveryStatus } from "../state/types"
import { DeliveryStatsDiff } from "./types"

export const useDeliveryOrderService = () => {
    const { buildDeliveryOrder, buildDeliveryOrderItem, buildDeliveryItemDeliveryStats } = useDeliveryOrderBuilders()
    const { buildItem } = useOperationBuilders()

    const { calculateDeliveryStatus } = deliveryOrderHelpers
    const { calculateDeliveryStatus: calculateOperationDeliveryStatus } = useOperationHooks()
    const { generateCode } = useDataTypesHooks()
    const { getPendingCredit } = useHoardHooks()

    const deliveryOrderLoaders = useDeliveryOrderLoaders()
    const saleOperationLoaders = useSaleOperationLoaders()

    const { updateOperationProps, updateDeliveryStats: updateOperationDeliveryStats } = useOperationService()
    const { saveDeliveryOrder } = useDeliveryOrderActions()
    const { enableStock } = useStockService()

    // The delivery order is read from the db (not the cached state) so consecutive updates don't overwrite each other
    const updateDeliveryOrderProps = async (
        deliveryOrderId: string,
        props: Partial<DeliveryOrder>,
    ) => {
        const { loadDeliveryOrder } = deliveryOrderLoaders()
        const loadedDeliveryOrder = await loadDeliveryOrder(deliveryOrderId, false)
        const deliveryOrder = buildDeliveryOrder({
            ...loadedDeliveryOrder!,
            ...props
        })
        return saveDeliveryOrder(deliveryOrder)
    }

    const updateDeliveryStats = async (
        deliveryOrderId: string,
        diffMap: Map<string, Partial<DeliveryStatsDiff>>,
        props: Partial<DeliveryOrder> = {}
    ) => {
        const { loadDeliveryOrder } = deliveryOrderLoaders()
        const loadedDeliveryOrder = await loadDeliveryOrder(deliveryOrderId, false)

        const items = props.items || loadedDeliveryOrder!.items
        const newItems = items.map(item => {
            const { deliveryStats } = item
            const diff = diffMap.get(item.itemId)
            const { quantity = 0, toBeDelivered = 0, pending = 0, dispatched = 0, delivered = 0, cancelled = 0, returned = 0 } = diff || {}
            return {
                ...item,
                quantity: Math.max(0, item.quantity + quantity),
                deliveryStats: buildDeliveryItemDeliveryStats({
                    toBeDelivered: deliveryStats!.toBeDelivered + toBeDelivered,
                    pending: Math.max(0, deliveryStats!.pending + pending),
                    dispatched: Math.max(0, deliveryStats!.dispatched + dispatched),
                    delivered: Math.max(0, deliveryStats!.delivered + delivered),
                    cancelled: Math.max(0, (deliveryStats!.cancelled || 0) + cancelled),
                    returned: Math.max(0, (deliveryStats!.returned || 0) + returned)
                })
            }
        })
        const updatedDeliveryOrder = { ...loadedDeliveryOrder!, items: newItems }

        return updateDeliveryOrderProps(deliveryOrderId, {
            deliveryStatus: calculateDeliveryStatus(updatedDeliveryOrder),
            ...props,
            items: newItems
        })
    }

    // A financial hoard only gets its items when emitting, so they never have pending quantities:
    // it stays partially delivered while it has pending credit to be consumed
    const refreshOperationDeliveryStatus = async (table: TableName, operationId: string) => {
        let deliveryStatus = await calculateOperationDeliveryStatus(table, operationId)
        if (deliveryStatus !== DeliveryStatus.PENDING) {
            const { loadSaleOperation } = saleOperationLoaders()
            const loadedOperation = await loadSaleOperation(table, operationId, true)
            if (loadedOperation && hoardHelpers.isFinancialHoard(table, loadedOperation) && getPendingCredit(loadedOperation as HoardOrder) > 0) {
                deliveryStatus = DeliveryStatus.PARTIALLY_DELIVERED
            }
        }
        return updateOperationProps(table, operationId, { deliveryStatus })
    }

    const createDeliveryOrder = async (
        operation: Operation,
        deliveryOrderData: Data2,
        deliveryOrderItemsData: Data2[]
    ) => {
        const table = deliveryOrderData.table as TableName
        const isSaleOperation = [TableName.SALE_ORDERS, TableName.HOARD_ORDERS].includes(table)

        const items = deliveryOrderItemsData.map(buildDeliveryOrderItem)
        const code = await generateCode(TableName.DELIVERY_ORDERS)
        let supplierId: O<string>, customerId: O<string>, branchId: string
        if (isSaleOperation) {
            const saleOperation = operation as SaleOperation
            customerId = saleOperation.customerId
            branchId = saleOperation.branchId
        } else {
            const purchase = operation as PurchaseOrder
            supplierId = purchase.supplierId
            branchId = purchase.deliveryBranchId
        }

        const deliveryOrder = buildDeliveryOrder({
            ...deliveryOrderData,
            code,
            items,
            supplierId,
            customerId,
            branchId
        })
        
        return saveDeliveryOrder(deliveryOrder)
    }

    const emitDeliveryOrder = async (
        deliveryOrderData: Data2,
        deliveryOrderItemsData: Data2[]
    ) => {
        const { loadSaleOperation } = saleOperationLoaders()
        const table = deliveryOrderData.table as TableName
        const operationId = deliveryOrderData.operationId as string
        // Read from the db: a stale operation would let the same pending quantities be emitted twice
        const loadedOperation = (await loadSaleOperation(table, operationId, true))!
        const updatesOperationStats = table !== TableName.PURCHASE_ORDERS && !hoardHelpers.isFinancialHoard(table, loadedOperation)

        if (updatesOperationStats) {
            const exceedsPending = deliveryOrderItemsData.some(itemData => {
                const operationItem = loadedOperation.items.find(item => item.id === itemData.itemId)
                return (itemData.quantity as number) > (operationItem?.deliveryStats.pending || 0)
            })
            if (exceedsPending) {
                throw new Error('Las cantidades pendientes de la operación cambiaron. Recargá la página y volvé a intentar.')
            }
        }

        const savedDeliveryOrder = await createDeliveryOrder(loadedOperation, deliveryOrderData, deliveryOrderItemsData)

        if (updatesOperationStats) {
            const diffMap = new Map<string, Partial<ItemDeliveryStats>>()
            savedDeliveryOrder.items.forEach(item => {
                diffMap.set(item.itemId, {
                    pending: -item.quantity,
                    emitted: item.quantity
                })
            })
            await updateOperationDeliveryStats(table, operationId, diffMap)
        }
        await refreshOperationDeliveryStatus(table, operationId)

        return savedDeliveryOrder
    }

    // Sales and product hoards: the annulled quantities go back to the operation pending, so their stock stays reserved.
    // Financial hoards: the items are only added when emitting an order, so the annulled quantities are cancelled
    // (lowering the used credit) and their reserved stock is released.
    const annulDeliveryOrder = async (deliveryOrderId: string) => {
        const { loadDeliveryOrder } = deliveryOrderLoaders()
        const loadedDeliveryOrder = (await loadDeliveryOrder(deliveryOrderId, false))!
        const { table, operationId } = loadedDeliveryOrder
        const { loadSaleOperation } = saleOperationLoaders()
        const loadedOperation = (await loadSaleOperation(table, operationId, true))!
        const isFinancialHoard = hoardHelpers.isFinancialHoard(table, loadedOperation)
        // An order with deliveries isn't cancelled: only its pending items are (it ends up delivered)
        const hasMovement = loadedDeliveryOrder.items.some(item => {
            const { dispatched, delivered, returned = 0 } = item.deliveryStats
            return dispatched + delivered + returned > 0
        })

        const orderDiffMap = new Map<string, Partial<DeliveryStatsDiff>>()
        const operationDiffMap = new Map<string, Partial<ItemDeliveryStats>>()
        const annulledQuantities = new Map<string, number>()
        loadedDeliveryOrder.items
            .filter(item => item.deliveryStats.pending > 0)
            .forEach(item => {
                const annulQuantity = item.deliveryStats.pending
                annulledQuantities.set(item.itemId, annulQuantity)
                orderDiffMap.set(item.itemId, {
                    pending: -annulQuantity,
                    toBeDelivered: -annulQuantity,
                    cancelled: annulQuantity
                })
                operationDiffMap.set(item.itemId, isFinancialHoard ? {
                    toBeEmitted: -annulQuantity,
                    emitted: -annulQuantity
                } : {
                    pending: annulQuantity,
                    emitted: -annulQuantity
                })
            })

        await updateDeliveryStats(deliveryOrderId, orderDiffMap, {
            cancelled: !hasMovement,
            notDeliveredCount: []
        })

        if (isFinancialHoard) {
            const updatedItems = loadedOperation.items.map(operationItem => {
                const annulledQuantity = annulledQuantities.get(operationItem.id) || 0
                return annulledQuantity > 0 ? buildItem({
                    ...operationItem,
                    cancelledQuantity: (operationItem.cancelledQuantity || 0) + annulledQuantity
                }) : operationItem
            })
            await updateOperationDeliveryStats(table, operationId, operationDiffMap, { items: updatedItems })
            for (const operationItem of loadedOperation.items) {
                const annulledQuantity = annulledQuantities.get(operationItem.id) || 0
                await enableStock(operationItem.productId, loadedOperation.branchId, annulledQuantity, operationItem.lotId)
            }
        } else {
            await updateOperationDeliveryStats(table, operationId, operationDiffMap)
        }
        await refreshOperationDeliveryStatus(table, operationId)
    }

    return {
        updateDeliveryOrderProps,
        updateDeliveryStats,
        refreshOperationDeliveryStatus,
        createDeliveryOrder,
        emitDeliveryOrder,
        annulDeliveryOrder
    }
}
